import React, { memo } from 'react';
import { 
  Files, 
  Search, 
  GitBranch, 
  Bug, 
  Settings,
  ChevronRight,
  ChevronDown,
  FileText,
  Folder,
  X
} from 'lucide-react';

const EditorContent = memo(({ activity }) => {
  const isTyping = activity?.action === 'type';
  const isScrolling = activity?.action === 'scroll';

  const files = [
    { name: 'src', type: 'folder', expanded: true },
    { name: 'components', type: 'folder', expanded: true, indent: 1 },
    { name: 'App.tsx', type: 'file', indent: 2, active: true },
    { name: 'Header.tsx', type: 'file', indent: 2 },
    { name: 'utils', type: 'folder', expanded: false, indent: 1 },
    { name: 'package.json', type: 'file', indent: 0 },
  ];

  return (
    <div className="vscode-content">
      {/* Activity Bar */}
      <div className="vscode-activity-bar">
        <div className="activity-icon active"><Files size={10} /></div>
        <div className="activity-icon"><Search size={10} /></div>
        <div className="activity-icon"><GitBranch size={10} /></div>
        <div className="activity-icon"><Bug size={10} /></div>
        <div className="activity-icon bottom"><Settings size={10} /></div>
      </div>

      {/* Sidebar */}
      <div className="vscode-sidebar">
        <div className="sidebar-header">
          <span className="sidebar-title">EXPLORER</span>
        </div>
        <div className="file-tree">
          <div className="tree-section">
            <div className="tree-section-header">
              <ChevronDown size={6} />
              <span>MY-APP</span>
            </div>
            {files.map((file, i) => (
              <div 
                key={i} 
                className={`tree-item ${file.active ? 'active' : ''}`}
                style={{ paddingLeft: `${8 + file.indent * 8}px` }}
              >
                {file.type === 'folder' && (
                  file.expanded ? <ChevronDown size={6} /> : <ChevronRight size={6} />
                )}
                {file.type === 'folder' ? <Folder size={7} /> : <FileText size={7} />}
                <span className="tree-item-name">{file.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="vscode-main">
        {/* Tabs */}
        <div className="editor-tabs">
          <div className="editor-tab active">
            <FileText size={7} />
            <span>App.tsx</span>
            <button className="tab-close"><X size={6} /></button>
          </div>
          <div className="editor-tab">
            <FileText size={7} />
            <span>Header.tsx</span>
          </div>
        </div>

        {/* Editor */}
        <div className={`code-editor ${isScrolling ? 'scrolling' : ''}`}>
          <div className="code-line">
            <span className="line-number">1</span>
            <span className="code-keyword">import</span>
            <span className="code-text"> React </span>
            <span className="code-keyword">from</span>
            <span className="code-string"> 'react'</span>
            <span className="code-text">;</span>
          </div>
          <div className="code-line">
            <span className="line-number">2</span>
            <span className="code-keyword">import</span>
            <span className="code-text"> </span>
            <span className="code-string">'./App.css'</span>
            <span className="code-text">;</span>
          </div>
          <div className="code-line">
            <span className="line-number">3</span>
          </div>
          <div className="code-line">
            <span className="line-number">4</span>
            <span className="code-keyword">function</span>
            <span className="code-function"> App</span>
            <span className="code-text">() {'{'}</span>
          </div>
          <div className="code-line">
            <span className="line-number">5</span>
            <span className="code-text">  </span>
            <span className="code-keyword">const</span>
            <span className="code-text"> [</span>
            <span className="code-variable">count</span>
            <span className="code-text">, </span>
            <span className="code-variable">setCount</span>
            <span className="code-text">] = </span>
            <span className="code-function">useState</span>
            <span className="code-text">(</span>
            <span className="code-number">0</span>
            <span className="code-text">);</span>
          </div>
          <div className="code-line">
            <span className="line-number">6</span>
          </div>
          <div className="code-line">
            <span className="line-number">7</span>
            <span className="code-text">  </span>
            <span className="code-keyword">return</span>
            <span className="code-text"> (</span>
          </div>
          <div className="code-line">
            <span className="line-number">8</span>
            <span className="code-text">    {'<'}</span>
            <span className="code-tag">div</span>
            <span className="code-text"> </span>
            <span className="code-attribute">className</span>
            <span className="code-text">=</span>
            <span className="code-string">"app"</span>
            <span className="code-text">{'>'}</span>
          </div>
          <div className="code-line">
            <span className="line-number">9</span>
            <span className="code-text">      {'<'}</span>
            <span className="code-tag">h1</span>
            <span className="code-text">{'>'}</span>
            <span className="code-text">Counter: </span>
            <span className="code-text">{'{'}</span>
            <span className="code-variable">count</span>
            <span className="code-text">{'}'}</span>
            <span className="code-text">{'</'}</span>
            <span className="code-tag">h1</span>
            <span className="code-text">{'>'}</span>
          </div>
          <div className="code-line">
            <span className="line-number">10</span>
            <span className="code-text">      {'<'}</span>
            <span className="code-tag">button</span>
            <span className="code-text"> </span>
            <span className="code-attribute">onClick</span>
            <span className="code-text">=</span>
            <span className="code-text">{'{'}</span>
            <span className="code-text">() </span>
            <span className="code-keyword">=></span>
            <span className="code-text"> </span>
            <span className="code-function">setCount</span>
            <span className="code-text">(</span>
            <span className="code-variable">count</span>
            <span className="code-text"> + </span>
            <span className="code-number">1</span>
            <span className="code-text">)</span>
            <span className="code-text">{'}'}</span>
            <span className="code-text">{'>'}</span>
          </div>
          <div className="code-line">
            <span className="line-number">11</span>
            <span className="code-text">        Increment</span>
          </div>
          <div className={`code-line ${isTyping ? 'typing' : ''}`}>
            <span className="line-number">12</span>
            <span className="code-text">      {'</'}</span>
            <span className="code-tag">button</span>
            <span className="code-text">{'>'}</span>
            {isTyping && <span className="editor-cursor"></span>}
          </div>
          <div className="code-line">
            <span className="line-number">13</span>
            <span className="code-text">    {'</'}</span>
            <span className="code-tag">div</span>
            <span className="code-text">{'>'}</span>
          </div>
          <div className="code-line">
            <span className="line-number">14</span>
            <span className="code-text">  );</span>
          </div>
          <div className="code-line">
            <span className="line-number">15</span>
            <span className="code-text">{'}'}</span>
          </div>
        </div>

        {/* Terminal */}
        <div className="vscode-terminal">
          <div className="terminal-header">
            <span className="terminal-title">TERMINAL</span>
            <div className="terminal-actions">
              <button className="terminal-btn">+</button>
              <button className="terminal-btn">×</button>
            </div>
          </div>
          <div className="terminal-content">
            <div className="terminal-line">
              <span className="terminal-prompt">$</span>
              <span className="terminal-command"> npm run dev</span>
            </div>
            <div className="terminal-line terminal-output">
              <span className="terminal-success">✓</span>
              <span> Local: http://localhost:3000</span>
            </div>
            <div className="terminal-line terminal-output">
              <span>  ready in 342ms</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

EditorContent.displayName = 'EditorContent';

export default EditorContent;
